import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Award,
  Briefcase,
  Calendar,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  CircleX,
  Clock,
  LogIn,
  RefreshCw,
  ShieldCheck,
  ShieldOff,
  Star,
  TrendingUp,
  Users,
  Wrench,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { formatDate, formatDateTime, initialsOf } from '@/lib/format';
import { useFeedback } from '@/components/Feedback';
import { HeroHeader, Lightbox, SoftBadge, Stars } from '@/components/ui';
import Spinner from '@/components/Spinner';
import styles from './Stats.module.css';

/* ── Types ────────────────────────────────────────────────────────────────── */

type Stats = {
  totalUsers: number;
  totalProviders: number;
  totalClients: number;
  totalBookings: number;
  pendingApplications: number;
  approvedApplications: number;
  rejectedApplications: number;
  bookingsPending: number;
  bookingsConfirmed: number;
  bookingsCompleted: number;
  bookingsCancelled: number;
  newUsersThisWeek: number;
  newBookingsThisWeek: number;
};

type UserItem = {
  id: string;
  full_name: string;
  account_type: string;
  created_at: string;
  is_banned: boolean;
  login_count?: number;
  last_login_at?: string | null;
};

type ProviderItem = {
  id: string;
  full_name: string;
  rating: number | null;
  is_banned: boolean;
  missionsCompleted: number;
  login_count: number;
  last_login_at: string | null;
};

type Review = {
  rating: number;
  review_comment: string | null;
  created_at: string;
  client_name: string;
};

type Mission = {
  id: string;
  status: string;
  created_at: string;
  counterpart: string;
  rating: number | null;
};

type PersonDetail = {
  id: string;
  full_name: string;
  is_banned: boolean;
  kind: 'provider' | 'client';
  missionsCompleted: number;
  reviews: Review[];
  missions: Mission[];
  rating: number | null;
  login_count: number;
  last_login_at: string | null;
};

type BookingMedia = {
  title: string;
  startPhoto: string | null;
  completionPhoto: string | null;
  images: { url: string; sender_name: string; created_at: string }[];
};

type ServiceStat = { id: string; name: string; totalBookings: number; completedBookings: number };

type DrillView = 'all' | 'clients' | 'providers' | 'services';

const EMPTY: Stats = {
  totalUsers: 0,
  totalProviders: 0,
  totalClients: 0,
  totalBookings: 0,
  pendingApplications: 0,
  approvedApplications: 0,
  rejectedApplications: 0,
  bookingsPending: 0,
  bookingsConfirmed: 0,
  bookingsCompleted: 0,
  bookingsCancelled: 0,
  newUsersThisWeek: 0,
  newBookingsThisWeek: 0,
};

/* ── Screen ───────────────────────────────────────────────────────────────── */

export default function AdminStats() {
  const { toast, confirm } = useFeedback();

  const [stats, setStats] = useState<Stats>(EMPTY);
  const [loading, setLoading] = useState(true);

  const [drillView, setDrillView] = useState<DrillView | null>(null);
  const [drillTitle, setDrillTitle] = useState('');
  const [drillLoading, setDrillLoading] = useState(false);
  const [usersList, setUsersList] = useState<UserItem[]>([]);
  const [providersList, setProvidersList] = useState<ProviderItem[]>([]);
  const [serviceStats, setServiceStats] = useState<ServiceStat[]>([]);

  const [detail, setDetail] = useState<PersonDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [bookingMedia, setBookingMedia] = useState<BookingMedia | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const counts = await Promise.all([
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        supabase
          .from('providers')
          .select('*', { count: 'exact', head: true })
          .eq('is_approved', true),
        supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .eq('account_type', 'client'),
        supabase.from('bookings').select('*', { count: 'exact', head: true }),
        supabase
          .from('provider_applications')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending'),
        supabase
          .from('provider_applications')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'approved'),
        supabase
          .from('provider_applications')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'rejected'),
        supabase.from('bookings').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase
          .from('bookings')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'confirmed'),
        supabase
          .from('bookings')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'completed'),
        supabase
          .from('bookings')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'cancelled'),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).gte('created_at', weekAgo),
        supabase.from('bookings').select('*', { count: 'exact', head: true }).gte('created_at', weekAgo),
      ]);

      const [
        totalUsers,
        totalProviders,
        totalClients,
        totalBookings,
        pendingApplications,
        approvedApplications,
        rejectedApplications,
        bookingsPending,
        bookingsConfirmed,
        bookingsCompleted,
        bookingsCancelled,
        newUsersThisWeek,
        newBookingsThisWeek,
      ] = counts.map((result) => result.count ?? 0);

      setStats({
        totalUsers,
        totalProviders,
        totalClients,
        totalBookings,
        pendingApplications,
        approvedApplications,
        rejectedApplications,
        bookingsPending,
        bookingsConfirmed,
        bookingsCompleted,
        bookingsCancelled,
        newUsersThisWeek,
        newBookingsThisWeek,
      });
    } catch (err) {
      console.error('Stats error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  /* ── Drill-downs ───────────────────────────────────────────────────────── */
  const openDrill = async (view: DrillView, title: string) => {
    setDrillView(view);
    setDrillTitle(title);
    setDrillLoading(true);
    setDetail(null);
    setBookingMedia(null);

    try {
      if (view === 'providers') {
        const { data: providerRows } = await supabase
          .from('providers')
          .select('id, rating, profiles(full_name, is_banned, login_count, last_login_at)')
          .eq('is_approved', true);

        const providerIds = (providerRows ?? []).map((row: any) => row.id);
        const missionCounts: Record<string, number> = {};

        if (providerIds.length > 0) {
          const { data: missions } = await supabase
            .from('bookings')
            .select('provider_id')
            .in('provider_id', providerIds)
            .in('status', ['pending_review', 'completed']);
          (missions ?? []).forEach((row: any) => {
            missionCounts[row.provider_id] = (missionCounts[row.provider_id] ?? 0) + 1;
          });
        }

        setProvidersList(
          (providerRows ?? [])
            .map((row: any) => ({
              id: row.id,
              full_name: row.profiles?.full_name ?? 'Inconnu',
              rating: row.rating ?? null,
              is_banned: row.profiles?.is_banned ?? false,
              missionsCompleted: missionCounts[row.id] ?? 0,
              login_count: row.profiles?.login_count ?? 0,
              last_login_at: row.profiles?.last_login_at ?? null,
            }))
            .sort((a, b) => b.missionsCompleted - a.missionsCompleted)
        );
      } else if (view === 'services') {
        const [{ data: services }, { data: bookings }] = await Promise.all([
          supabase.from('services').select('id, name'),
          supabase.from('bookings').select('service_id, status'),
        ]);

        const totals: Record<string, number> = {};
        const done: Record<string, number> = {};
        (bookings ?? []).forEach((row: any) => {
          totals[row.service_id] = (totals[row.service_id] ?? 0) + 1;
          if (row.status === 'completed') done[row.service_id] = (done[row.service_id] ?? 0) + 1;
        });

        setServiceStats(
          (services ?? [])
            .map((row: any) => ({
              id: row.id,
              name: row.name,
              totalBookings: totals[row.id] ?? 0,
              completedBookings: done[row.id] ?? 0,
            }))
            .sort((a, b) => b.totalBookings - a.totalBookings)
        );
      } else {
        let query = supabase
          .from('profiles')
          .select('id, full_name, account_type, created_at, is_banned, login_count, last_login_at')
          .order('created_at', { ascending: false });
        if (view === 'clients') query = query.eq('account_type', 'client');

        const { data } = await query;
        setUsersList(
          (data ?? []).map((row: any) => ({ ...row, is_banned: row.is_banned ?? false }))
        );
      }
    } finally {
      setDrillLoading(false);
    }
  };

  const openProviderDetail = async (provider: ProviderItem) => {
    setDetailLoading(true);
    try {
      const [{ data: profile }, { data: bookingRows }] = await Promise.all([
        supabase
          .from('profiles')
          .select('login_count, last_login_at')
          .eq('id', provider.id)
          .maybeSingle(),
        supabase
          .from('bookings')
          .select('id, status, created_at, rating, client_id')
          .eq('provider_id', provider.id)
          .in('status', ['pending_review', 'completed'])
          .order('created_at', { ascending: false }),
      ]);

      const { data: reviewRows } = await supabase
        .from('bookings')
        .select('rating, review_comment, created_at, client_id')
        .eq('provider_id', provider.id)
        .not('rating', 'is', null)
        .order('created_at', { ascending: false });

      const clientIds = [
        ...new Set([
          ...(bookingRows ?? []).map((row: any) => row.client_id),
          ...(reviewRows ?? []).map((row: any) => row.client_id),
        ]),
      ].filter(Boolean);

      const nameMap: Record<string, string> = {};
      if (clientIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', clientIds);
        (profiles ?? []).forEach((row: any) => {
          nameMap[row.id] = row.full_name;
        });
      }

      const reviews: Review[] = (reviewRows ?? []).map((row: any) => ({
        rating: row.rating,
        review_comment: row.review_comment ?? null,
        created_at: row.created_at,
        client_name: nameMap[row.client_id] ?? 'Client',
      }));

      const liveRating =
        reviews.length > 0
          ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length
          : null;

      setDetail({
        id: provider.id,
        full_name: provider.full_name,
        is_banned: provider.is_banned,
        kind: 'provider',
        missionsCompleted: bookingRows?.length ?? 0,
        reviews,
        missions: (bookingRows ?? []).map((row: any) => ({
          id: row.id,
          status: row.status,
          created_at: row.created_at,
          counterpart: nameMap[row.client_id] ?? 'Client',
          rating: row.rating ?? null,
        })),
        rating: liveRating,
        login_count: (profile as any)?.login_count ?? 0,
        last_login_at: (profile as any)?.last_login_at ?? null,
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const openClientDetail = async (user: UserItem) => {
    setDetailLoading(true);
    try {
      const [{ data: bookingRows }, { data: profile }] = await Promise.all([
        supabase
          .from('bookings')
          .select('id, status, created_at, rating, provider_id')
          .eq('client_id', user.id)
          .order('created_at', { ascending: false }),
        supabase
          .from('profiles')
          .select('login_count, last_login_at')
          .eq('id', user.id)
          .maybeSingle(),
      ]);

      const providerIds = [
        ...new Set((bookingRows ?? []).map((row: any) => row.provider_id).filter(Boolean)),
      ];
      const nameMap: Record<string, string> = {};
      if (providerIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', providerIds);
        (profiles ?? []).forEach((row: any) => {
          nameMap[row.id] = row.full_name;
        });
      }

      setDetail({
        id: user.id,
        full_name: user.full_name,
        is_banned: user.is_banned,
        kind: 'client',
        missionsCompleted: (bookingRows ?? []).filter((row: any) => row.status === 'completed')
          .length,
        reviews: [],
        missions: (bookingRows ?? []).map((row: any) => ({
          id: row.id,
          status: row.status,
          created_at: row.created_at,
          counterpart: nameMap[row.provider_id] ?? 'Non assigné',
          rating: row.rating ?? null,
        })),
        rating: null,
        login_count: (profile as any)?.login_count ?? 0,
        last_login_at: (profile as any)?.last_login_at ?? null,
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const openBookingMedia = async (bookingId: string, title: string) => {
    setDetailLoading(true);
    try {
      const [{ data: booking }, { data: room }] = await Promise.all([
        supabase
          .from('bookings')
          .select('completion_photo, start_photo')
          .eq('id', bookingId)
          .maybeSingle(),
        supabase.from('chat_rooms').select('id').eq('booking_id', bookingId).maybeSingle(),
      ]);

      let images: BookingMedia['images'] = [];
      if (room) {
        const { data: messages } = await supabase
          .from('chat_messages')
          .select('attachment_url, attachment_type, sender_id, created_at')
          .eq('chat_room_id', (room as any).id)
          .in('attachment_type', ['image', 'video'])
          .order('created_at', { ascending: true });

        if (messages && messages.length > 0) {
          const senderIds = [...new Set(messages.map((row: any) => row.sender_id))];
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, full_name')
            .in('id', senderIds);
          const nameMap: Record<string, string> = {};
          (profiles ?? []).forEach((row: any) => {
            nameMap[row.id] = row.full_name;
          });

          images = messages.map((row: any) => ({
            url: row.attachment_url,
            sender_name: nameMap[row.sender_id] ?? 'Inconnu',
            created_at: row.created_at,
          }));
        }
      }

      setBookingMedia({
        title,
        startPhoto: (booking as any)?.start_photo ?? null,
        completionPhoto: (booking as any)?.completion_photo ?? null,
        images,
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleBan = async (person: PersonDetail) => {
    const ban = !person.is_banned;
    const ok = await confirm({
      title: ban ? `Bannir ${person.full_name} ?` : `Débannir ${person.full_name} ?`,
      body: ban
        ? 'Ce compte sera immédiatement déconnecté et bloqué.'
        : 'Ce compte sera réactivé.',
      confirmLabel: ban ? 'Bannir' : 'Débannir',
      destructive: ban,
    });
    if (!ok) return;

    const { error } = await supabase
      .from('profiles')
      .update({ is_banned: ban })
      .eq('id', person.id);

    if (error) {
      toast('error', 'Action impossible', error.message);
      return;
    }

    setDetail((current) => (current ? { ...current, is_banned: ban } : current));
    setProvidersList((list) =>
      list.map((item) => (item.id === person.id ? { ...item, is_banned: ban } : item))
    );
    setUsersList((list) =>
      list.map((item) => (item.id === person.id ? { ...item, is_banned: ban } : item))
    );
    toast('success', ban ? 'Compte banni' : 'Compte réactivé');
  };

  const closeDrill = () => {
    setDrillView(null);
    setDetail(null);
    setBookingMedia(null);
  };

  const goBack = () => {
    if (bookingMedia) return setBookingMedia(null);
    if (detail) return setDetail(null);
    closeDrill();
  };

  const panelTitle = bookingMedia?.title ?? detail?.full_name ?? drillTitle;

  return (
    <div className={styles.page}>
      <HeroHeader title="Statistiques" subtitle="Vue d'ensemble de la plateforme.">
        <button
          type="button"
          className={styles.refreshBtn}
          onClick={() => void loadStats()}
          aria-label="Rafraîchir"
        >
          <RefreshCw size={17} /> Rafraîchir
        </button>
      </HeroHeader>

      <div className={styles.body}>
        {loading ? (
          <Spinner block />
        ) : (
          <>
            <Group title="Activité récente (7 jours)">
              <StatCard
                icon={<TrendingUp size={22} color="#9C27B0" />}
                label="Nouveaux inscrits"
                value={stats.newUsersThisWeek}
                color="#F3E5F5"
              />
              <StatCard
                icon={<Calendar size={22} color="#FF6F00" />}
                label="Nouvelles réservations"
                value={stats.newBookingsThisWeek}
                color="#FFF3E0"
              />
            </Group>

            <Group title="Utilisateurs">
              <StatCard
                icon={<Users size={22} color="#1B4183" />}
                label="Total inscrits"
                value={stats.totalUsers}
                color="#E8EFFE"
                onClick={() => void openDrill('all', 'Tous les inscrits')}
              />
              <StatCard
                icon={<Users size={22} color="#4CAF50" />}
                label="Clients"
                value={stats.totalClients}
                color="#E8F5E9"
                onClick={() => void openDrill('clients', 'Clients')}
              />
              <StatCard
                icon={<Briefcase size={22} color="#FF8400" />}
                label="Prestataires actifs"
                value={stats.totalProviders}
                color="#FFF3E0"
                onClick={() => void openDrill('providers', 'Prestataires')}
              />
              <StatCard
                icon={<Wrench size={22} color="#00897B" />}
                label="Analyse par service"
                value={stats.totalBookings}
                color="#E0F2F1"
                onClick={() => void openDrill('services', 'Services')}
              />
            </Group>

            <Group title="Demandes prestataires">
              <StatCard
                icon={<Clock size={22} color="#FFA900" />}
                label="En attente"
                value={stats.pendingApplications}
                color="#FFFDE7"
              />
              <StatCard
                icon={<CircleCheck size={22} color="#4CAF50" />}
                label="Approuvées"
                value={stats.approvedApplications}
                color="#E8F5E9"
              />
              <StatCard
                icon={<CircleX size={22} color="#F44336" />}
                label="Rejetées"
                value={stats.rejectedApplications}
                color="#FFEBEE"
              />
            </Group>

            <Group title="Réservations">
              <StatCard
                icon={<Calendar size={22} color="#1B4183" />}
                label="Total"
                value={stats.totalBookings}
                color="#E8EFFE"
              />
              <StatCard
                icon={<Clock size={22} color="#FFA900" />}
                label="En attente"
                value={stats.bookingsPending}
                color="#FFFDE7"
              />
              <StatCard
                icon={<CircleCheck size={22} color="#2196F3" />}
                label="Confirmées"
                value={stats.bookingsConfirmed}
                color="#E3F2FD"
              />
              <StatCard
                icon={<CircleCheck size={22} color="#4CAF50" />}
                label="Terminées"
                value={stats.bookingsCompleted}
                color="#E8F5E9"
              />
              <StatCard
                icon={<CircleX size={22} color="#9E9E9E" />}
                label="Annulées"
                value={stats.bookingsCancelled}
                color="#F5F5F5"
              />
            </Group>
          </>
        )}
      </div>

      {/* ── Drill-down panel ── */}
      {drillView ? (
        <div className={styles.drillOverlay} role="dialog" aria-modal="true" onClick={closeDrill}>
          <div className={styles.drillPanel} onClick={(event) => event.stopPropagation()}>
            <header className={styles.drillHeader}>
              <button
                type="button"
                className={styles.drillBack}
                onClick={goBack}
                aria-label="Retour"
              >
                <ChevronLeft size={22} />
              </button>
              <h2 className={styles.drillTitle}>{panelTitle}</h2>
              <button type="button" className={styles.drillClose} onClick={closeDrill}>
                Fermer
              </button>
            </header>

            <div className={styles.drillBody}>
              {drillLoading || detailLoading ? (
                <Spinner block />
              ) : bookingMedia ? (
                <BookingMediaView media={bookingMedia} onOpen={setLightbox} />
              ) : detail ? (
                <PersonDetailView
                  detail={detail}
                  onToggleBan={() => void toggleBan(detail)}
                  onOpenMission={openBookingMedia}
                />
              ) : drillView === 'services' ? (
                <ServiceBreakdown stats={serviceStats} />
              ) : drillView === 'providers' ? (
                <ul className={styles.rowList}>
                  {providersList.length === 0 ? (
                    <p className={styles.empty}>Aucun prestataire</p>
                  ) : null}
                  {providersList.map((provider) => (
                    <li key={provider.id}>
                      <button
                        type="button"
                        className={`${styles.rowItem} ${provider.is_banned ? styles.rowBanned : ''}`}
                        onClick={() => void openProviderDetail(provider)}
                      >
                        <span className={styles.rowAvatar}>{initialsOf(provider.full_name)}</span>
                        <span className={styles.rowInfo}>
                          <span className={styles.rowName}>{provider.full_name}</span>
                          <span className={styles.rowSub}>
                            {provider.is_banned ? 'Banni · ' : ''}
                            {provider.missionsCompleted} mission
                            {provider.missionsCompleted !== 1 ? 's' : ''} · {provider.login_count}{' '}
                            connexion{provider.login_count !== 1 ? 's' : ''}
                          </span>
                        </span>
                        {provider.rating != null && provider.rating > 0 ? (
                          <span className={styles.ratingPill}>
                            <Star size={12} color="#FF8400" fill="#FF8400" />
                            {Number(provider.rating).toFixed(1)}
                          </span>
                        ) : null}
                        <ChevronRight size={18} color="#ccc" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className={styles.rowList}>
                  {usersList.length === 0 ? <p className={styles.empty}>Aucun utilisateur</p> : null}
                  {usersList.map((user) => {
                    const isClient = user.account_type === 'client';
                    return (
                      <li key={user.id}>
                        <button
                          type="button"
                          className={`${styles.rowItem} ${user.is_banned ? styles.rowBanned : ''}`}
                          onClick={() => isClient && void openClientDetail(user)}
                          disabled={!isClient}
                        >
                          <span className={styles.rowAvatar}>{initialsOf(user.full_name)}</span>
                          <span className={styles.rowInfo}>
                            <span className={styles.rowName}>{user.full_name}</span>
                            <span className={styles.rowSub}>
                              {user.is_banned ? 'Banni · ' : ''}
                              {isClient ? 'Client' : 'Prestataire'} · {formatDate(user.created_at)}
                            </span>
                            <span className={styles.rowMeta}>
                              {user.login_count ?? 0} connexion
                              {(user.login_count ?? 0) !== 1 ? 's' : ''}
                              {user.last_login_at ? ` · ${formatDate(user.last_login_at)}` : ''}
                            </span>
                          </span>
                          {isClient ? <ChevronRight size={18} color="#ccc" /> : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}

/* ── Sub-views ────────────────────────────────────────────────────────────── */

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={styles.group}>
      <h2 className={styles.groupTitle}>{title}</h2>
      <div className={styles.grid}>{children}</div>
    </section>
  );
}

function StatCard({
  icon,
  label,
  value,
  color,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  color: string;
  onClick?: () => void;
}) {
  const content = (
    <>
      <span className={styles.statIcon}>{icon}</span>
      <span className={styles.statValue}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
      {onClick ? <span className={styles.statLink}>Voir détails →</span> : null}
    </>
  );

  return onClick ? (
    <button type="button" className={styles.statCard} style={{ background: color }} onClick={onClick}>
      {content}
    </button>
  ) : (
    <div className={styles.statCard} style={{ background: color }}>
      {content}
    </div>
  );
}

function PersonDetailView({
  detail,
  onToggleBan,
  onOpenMission,
}: {
  detail: PersonDetail;
  onToggleBan: () => void;
  onOpenMission: (bookingId: string, title: string) => void;
}) {
  const cancelled = detail.missions.filter((mission) => mission.status === 'cancelled').length;

  return (
    <div className={styles.detail}>
      <div className={styles.detailCards}>
        <div className={styles.detailCard} style={{ background: '#E8F5E9' }}>
          <CircleCheck size={22} color="#2E7D32" />
          <span className={styles.detailValue}>{detail.missionsCompleted}</span>
          <span className={styles.detailLabel}>
            {detail.kind === 'provider' ? 'Missions réalisées' : 'Demandes terminées'}
          </span>
        </div>

        {detail.kind === 'provider' ? (
          <>
            <div className={styles.detailCard} style={{ background: '#FFF3E0' }}>
              <Star size={22} color="#FF8400" fill={detail.rating != null ? '#FF8400' : 'none'} />
              <span className={styles.detailValue}>
                {detail.rating != null ? Number(detail.rating).toFixed(1) : '—'}
              </span>
              <span className={styles.detailLabel}>Note moyenne</span>
            </div>
            <div className={styles.detailCard} style={{ background: '#E8EFFE' }}>
              <Award size={22} color="#1B4183" />
              <span className={styles.detailValue}>{detail.reviews.length}</span>
              <span className={styles.detailLabel}>Avis reçus</span>
            </div>
          </>
        ) : (
          <>
            <div className={styles.detailCard} style={{ background: '#E8EFFE' }}>
              <Calendar size={22} color="#1B4183" />
              <span className={styles.detailValue}>{detail.missions.length}</span>
              <span className={styles.detailLabel}>Total demandes</span>
            </div>
            <div className={styles.detailCard} style={{ background: '#FFEBEE' }}>
              <CircleX size={22} color="#F44336" />
              <span className={styles.detailValue}>{cancelled}</span>
              <span className={styles.detailLabel}>Annulées</span>
            </div>
          </>
        )}

        <div className={styles.detailCard} style={{ background: '#F3E5F5' }}>
          <LogIn size={22} color="#9C27B0" />
          <span className={styles.detailValue}>{detail.login_count}</span>
          <span className={styles.detailLabel}>Connexions</span>
        </div>
        <div className={styles.detailCard} style={{ background: '#EDE7F6' }}>
          <Clock size={22} color="#673AB7" />
          <span className={styles.detailValueSmall}>{formatDateTime(detail.last_login_at)}</span>
          <span className={styles.detailLabel}>Dernière connexion</span>
        </div>
      </div>

      <button
        type="button"
        className={`${styles.banBtn} ${detail.is_banned ? styles.banBtnActive : ''}`}
        onClick={onToggleBan}
      >
        {detail.is_banned ? <ShieldCheck size={18} /> : <ShieldOff size={18} />}
        {detail.is_banned ? 'Débannir ce compte' : 'Bannir ce compte'}
      </button>

      {detail.reviews.length > 0 ? (
        <section className={styles.detailSection}>
          <h3 className={styles.detailSectionTitle}>Avis clients</h3>
          <ul className={styles.reviewList}>
            {detail.reviews.map((review, index) => (
              <li key={index} className={styles.reviewCard}>
                <div className={styles.reviewHead}>
                  <Stars value={review.rating} size={14} />
                  <span className={styles.reviewClient}>{review.client_name}</span>
                  <span className={styles.reviewDate}>{formatDate(review.created_at)}</span>
                </div>
                {review.review_comment ? (
                  <p className={styles.reviewComment}>{review.review_comment}</p>
                ) : (
                  <p className={styles.reviewNoComment}>Pas de commentaire</p>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {detail.missions.length > 0 ? (
        <section className={styles.detailSection}>
          <h3 className={styles.detailSectionTitle}>
            {detail.kind === 'provider' ? 'Missions' : 'Historique des demandes'} (
            {detail.missions.length})
          </h3>
          <ul className={styles.missionList}>
            {detail.missions.map((mission) => (
              <li key={mission.id}>
                <button
                  type="button"
                  className={styles.missionItem}
                  onClick={() => onOpenMission(mission.id, `Photos · ${mission.counterpart}`)}
                >
                  <div className={styles.missionTop}>
                    <span className={styles.missionName}>{mission.counterpart}</span>
                    <SoftBadge status={mission.status} />
                  </div>
                  <div className={styles.missionBottom}>
                    <span className={styles.missionDate}>{formatDate(mission.created_at)}</span>
                    {mission.rating != null ? <Stars value={mission.rating} size={12} /> : null}
                    <span className={styles.missionLink}>Voir photos →</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function BookingMediaView({
  media,
  onOpen,
}: {
  media: BookingMedia;
  onOpen: (url: string) => void;
}) {
  const hasContent = media.startPhoto || media.completionPhoto || media.images.length > 0;

  return (
    <div className={styles.detail}>
      {!hasContent ? <p className={styles.empty}>Aucune photo pour cette mission</p> : null}

      {media.startPhoto ? (
        <section className={styles.detailSection}>
          <h3 className={styles.detailSectionTitle}>Photo de début (état initial)</h3>
          <button type="button" onClick={() => onOpen(media.startPhoto!)}>
            <img className={styles.mediaFull} src={media.startPhoto} alt="État initial" />
          </button>
        </section>
      ) : null}

      {media.completionPhoto ? (
        <section className={styles.detailSection}>
          <h3 className={styles.detailSectionTitle}>Photo de fin (résultat)</h3>
          <button type="button" onClick={() => onOpen(media.completionPhoto!)}>
            <img className={styles.mediaFull} src={media.completionPhoto} alt="Résultat" />
          </button>
        </section>
      ) : null}

      {media.images.length > 0 ? (
        <section className={styles.detailSection}>
          <h3 className={styles.detailSectionTitle}>Photos du chat ({media.images.length})</h3>
          <div className={styles.mediaGrid}>
            {media.images.map((image, index) => (
              <button
                key={index}
                type="button"
                className={styles.mediaGridItem}
                onClick={() => onOpen(image.url)}
              >
                <img className={styles.mediaGridImage} src={image.url} alt="" />
                <span className={styles.mediaSender}>{image.sender_name}</span>
                <span className={styles.mediaDate}>{formatDate(image.created_at)}</span>
              </button>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function ServiceBreakdown({ stats }: { stats: ServiceStat[] }) {
  const max = Math.max(...stats.map((item) => item.totalBookings), 1);

  return (
    <div className={styles.detail}>
      <h3 className={styles.detailSectionTitle}>Réservations par service</h3>
      {stats.length === 0 ? <p className={styles.empty}>Aucune donnée</p> : null}

      <ul className={styles.serviceList}>
        {stats.map((service) => (
          <li key={service.id} className={styles.serviceCard}>
            <div className={styles.serviceHead}>
              <span className={styles.serviceName}>{service.name}</span>
              <span className={styles.serviceTotal}>
                {service.totalBookings} réservation{service.totalBookings !== 1 ? 's' : ''}
              </span>
            </div>
            <div className={styles.bar}>
              <div
                className={styles.barFill}
                style={{ width: `${(service.totalBookings / max) * 100}%` }}
              />
            </div>
            <div className={styles.serviceFoot}>
              <span className={styles.serviceDone}>
                {service.completedBookings} terminée{service.completedBookings !== 1 ? 's' : ''}
              </span>
              <span className={styles.serviceRate}>
                {service.totalBookings > 0
                  ? Math.round((service.completedBookings / service.totalBookings) * 100)
                  : 0}
                % de complétion
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
